import React, { useEffect, useState } from "react";
import { Users, Wallet, GraduationCap, TrendingUp, AlertCircle, FileText, BookOpen, ChevronRight, Sparkles, Eye, Mail } from "lucide-react";
import { api, fmtGNF } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend, Line, LineChart, CartesianGrid, Area, AreaChart } from "recharts";
import { Link } from "react-router-dom";
import AIInsightsCard from "@/components/AIInsightsCard";
import { PresenceWidget } from "@/components/PresenceWidget";

const KPICard = ({ icon: Icon, label, value, accent = "#166534", testId, sub }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 hover:border-[#166534] transition-colors" data-testid={testId}>
    <div className="flex items-start justify-between mb-3">
      <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">{label}</div>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}15`, color: accent }}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
    <div className="font-heading font-extrabold text-3xl text-[#0F172A]">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

export default function FounderDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [viewsSeries, setViewsSeries] = useState([]);
  const [catStats, setCatStats] = useState(null);

  const load = () => {
    setError(null);
    api.get("/dashboard/founder").then(({ data }) => setData(data)).catch((e) => setError({
      status: e?.response?.status || 0,
      detail: e?.response?.data?.detail || e?.message || "Erreur réseau",
    }));
    api.get("/dashboard/views-over-time?days=30").then(({ data }) => setViewsSeries(data)).catch(() => {});
    api.get("/dashboard/category-stats").then(({ data }) => setCatStats(data)).catch(() => {});
  };
  useEffect(load, []);

  if (!data && error) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center max-w-md" data-testid="dashboard-error">
        <AlertCircle className="h-8 w-8 text-amber-500 mx-auto mb-3" />
        <div className="font-heading font-bold text-[#0F172A]">Impossible de charger le tableau de bord</div>
        <p className="text-sm text-slate-500 mt-1">Vérifiez votre connexion Internet, puis réessayez.</p>
        <p className="text-[11px] text-slate-400 mt-2 font-mono" data-testid="dashboard-error-detail">
          Détail technique : {error.status ? `HTTP ${error.status} — ` : ""}{String(error.detail).slice(0, 160)}
        </p>
        <button onClick={load} className="mt-4 px-5 py-2 rounded-lg bg-[#1E3A8A] text-white text-sm font-semibold hover:bg-[#172554]" data-testid="dashboard-retry-btn">
          Réessayer
        </button>
      </div>
    );
  }
  if (!data) return <div className="text-slate-500">Chargement du tableau de bord...</div>;

  const revenueData = [
    { name: "Encaissé", value: data.revenue_paid, color: "#166534" },
    { name: "Attendu", value: data.revenue_pending, color: "#FBBF24" },
  ];

  const monthlyData = [
    { mois: "Sept", recettes: 12500000 },
    { mois: "Oct", recettes: 18900000 },
    { mois: "Nov", recettes: 15300000 },
    { mois: "Déc", recettes: 17200000 },
    { mois: "Jan", recettes: 19800000 },
    { mois: "Fév", recettes: 16400000 },
  ];

  return (
    <div className="space-y-6" data-testid="founder-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><Sparkles className="h-3 w-3" /><span>Vue stratégique</span></div>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] tracking-tight">Bonjour, voici la situation de l&apos;école</h2>
        </div>
        <Link to="/dashboard/ai" className="bg-[#EA580C] text-white px-5 py-2.5 rounded-lg font-semibold text-sm flex items-center gap-2 hover:bg-orange-700 transition" data-testid="ai-shortcut-btn">
          <Sparkles className="h-4 w-4" /> Demander à MAZID AI
        </Link>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard icon={Users} label="Effectif total" value={data.students_count} accent="#1E3A8A" testId="kpi-students" sub={`${data.classes_count} classes`} />
        <KPICard icon={GraduationCap} label="Enseignants" value={data.teachers_count} accent="#166534" testId="kpi-teachers" />
        <KPICard icon={Wallet} label="Recettes encaissées" value={fmtGNF(data.revenue_paid)} accent="#FBBF24" testId="kpi-revenue" sub={`Taux: ${data.recovery_rate}%`} />
        <KPICard icon={AlertCircle} label="Impayés" value={fmtGNF(data.revenue_pending)} accent="#EA580C" testId="kpi-pending" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard icon={TrendingUp} label="Moyenne générale" value={`${data.average_grade}/20`} accent="#166534" testId="kpi-avg" />
        <KPICard icon={Users} label="Présence" value={`${data.attendance_rate}%`} accent="#1E3A8A" testId="kpi-attendance" />
        <KPICard icon={FileText} label="Inscriptions en attente" value={data.enrollments_pending} accent="#EA580C" testId="kpi-enroll-pending" />
        <KPICard icon={AlertCircle} label="Nouvelles réclamations" value={data.complaints_new} accent="#FBBF24" testId="kpi-complaints" />
      </div>

      {/* Présence temps réel */}
      <PresenceWidget />

      {/* AI Insights — synthèse intelligente du jour */}
      <AIInsightsCard accentColor="#EA580C" />

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6" data-testid="revenue-chart">
          <h3 className="font-heading font-bold text-lg text-[#1E3A8A] mb-1">Évolution des recettes</h3>
          <p className="text-xs text-slate-500 mb-5">6 derniers mois (en GNF)</p>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="mois" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `${(v/1000000).toFixed(0)}M`} />
              <Tooltip formatter={(v) => fmtGNF(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
              <Line type="monotone" dataKey="recettes" stroke="#166534" strokeWidth={3} dot={{ fill: "#166534", r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="recovery-chart">
          <h3 className="font-heading font-bold text-lg text-[#1E3A8A] mb-1">Recouvrement</h3>
          <p className="text-xs text-slate-500 mb-5">Encaissé vs attendu</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={revenueData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value">
                {revenueData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtGNF(v)} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Engagement (views over time + category stats) */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6" data-testid="views-chart">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-heading font-bold text-lg text-[#1E3A8A] flex items-center gap-2"><Eye className="h-5 w-5" /> Vues des annonces</h3>
            <span className="text-xs text-slate-500">30 derniers jours</span>
          </div>
          <p className="text-xs text-slate-500 mb-5">Total : <strong>{viewsSeries.reduce((s, p) => s + p.views, 0)}</strong> vues</p>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={viewsSeries}>
              <defs>
                <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#166534" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#166534" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
              <Area type="monotone" dataKey="views" stroke="#166534" strokeWidth={2.5} fill="url(#viewsGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="category-stats">
          <h3 className="font-heading font-bold text-lg text-[#1E3A8A] flex items-center gap-2 mb-1"><TrendingUp className="h-5 w-5" /> Engagement par catégorie</h3>
          <p className="text-xs text-slate-500 mb-5">
            {catStats?.total_views || 0} vues · {catStats?.total_enrollments || 0} inscriptions · <strong className="text-[#166534]">{catStats?.global_conversion_rate || 0}% conversion</strong>
          </p>
          <div className="space-y-3">
            {(catStats?.by_category || []).map((c, i) => {
              const colors = ["#166534", "#EA580C", "#dc2626", "#1E3A8A", "#FBBF24"];
              return (
                <div key={i} data-testid={`cat-stat-${c.category}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-semibold text-slate-700">{c.label}</span>
                    <span className="text-slate-500"><strong className="text-[#0F172A]">{c.views}</strong> vues ({c.conversion_share}%)</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${c.conversion_share}%`, backgroundColor: colors[i % colors.length] }} />
                  </div>
                </div>
              );
            })}
            {(catStats?.by_category || []).every(c => c.views === 0) && (
              <p className="text-xs text-slate-400 text-center py-6">Aucune vue enregistrée pour le moment. Les statistiques apparaîtront dès que les visiteurs consulteront vos annonces.</p>
            )}
          </div>
        </div>
      </div>

      {/* AI Alerts */}
      <div className="bg-gradient-to-br from-[#1E3A8A] to-[#166534] text-white rounded-xl p-7 lg:p-8" data-testid="ai-recommendations">        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="chip bg-white/10 border-white/20 text-[#FBBF24] mb-3"><Sparkles className="h-3 w-3" /><span>MAZID AI — Recommandations</span></div>
            <h3 className="font-heading font-bold text-2xl">Vos priorités cette semaine</h3>
          </div>
          <Link to="/dashboard/ai" className="text-[#FBBF24] text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all" data-testid="ai-full-link">
            Conversation complète <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { title: "Recouvrement à intensifier", text: `${data.enrollments_pending > 0 ? data.enrollments_pending : 4} dossiers en attente nécessitent un suivi sous 48h.`, color: "border-l-[#FBBF24]" },
            { title: "Performance académique", text: `Moyenne ${data.average_grade}/20 — proposer un soutien aux classes en-dessous de 12/20.`, color: "border-l-[#166534]" },
            { title: "Communication parents", text: `${data.complaints_new} réclamation(s) à traiter en priorité pour maintenir la satisfaction.`, color: "border-l-[#EA580C]" },
          ].map((t, i) => (
            <div key={i} className={`bg-white/5 border border-white/10 ${t.color} border-l-4 rounded-lg p-4 backdrop-blur`} data-testid={`ai-reco-${i}`}>
              <div className="font-semibold text-sm mb-2">{t.title}</div>
              <div className="text-xs text-slate-200 leading-relaxed">{t.text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
